import { useEffect, useMemo, useState } from "react";
import { Card, Container, Button, Form, Input, Label } from "reactstrap";

import ReactTable from "components/ReactTable/ReactTable";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import styles from "../Support/Support.module.css";
import useAlertContext from "Hooks/useAlertContext";

interface ITask {
    id: number;
    refNumber: number;
    title: string;
    text: string;
    description: string;
    status: { id: number; name: string; color: string } | null;
    priority: { id: number; name: string; color: string } | null;
    assignedTo: { id: number; name: string } | null;
    project: { id: number; name: string } | null;
    tags: { id: number; name: string }[] | null;
    dueDate: string | null;
    lastUpdated: string | null; // task-search rows carry this
    createdOn: string | null; // task-detail rows (freshly created) carry this
}

interface ITaskSearchResult {
    totalTaskCount: number;
    listTasks: ITask[];
}

export default function SupportV2() {
    const [tasks, setTasks] = useState<ITask[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        subject: "",
        description: "",
    });

    const { newAlert } = useAlertContext();
    const { handleFormChange } = useHelpers();
    const { get, post } = useFetch();

    // Fetch tasks on load
    useEffect(() => {
        let ignore = false;
        getTasks();

        async function getTasks() {
            setIsLoading(true);
            const response = await get("/api/support/v2/tasks");

            if (ignore) return;

            if (response.ok) {
                const data: ITaskSearchResult = await response.json();
                setTasks(data.listTasks ?? []);
            }

            setIsLoading(false);
        }

        return () => {
            ignore = true;
        };
    }, [get]);

    async function submitTask(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        const body = {
            title: formData.subject,
            description: formData.description,
        };

        const response = await post("/api/support/v2/task", body);

        if (response.ok) {
            const newTask = await response.json();
            newAlert({
                color: "info",
                message: "Issue Submitted",
            });
            setFormData({ subject: "", description: "" });
            setTasks((prev) => [newTask, ...prev]);
        } else {
            newAlert({
                color: "danger",
                message: "Issue could not be submitted",
            });
        }
    }

    const columns = useMemo(() => {
        return [
            {
                Header: "Issue",
                accessor: "title",
                Cell: ({ value }: { value: string }) => value ?? "-",
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ value }: { value: { name: string } | null }) =>
                    value?.name ?? "-",
            },
            {
                // Search rows expose lastUpdated (no createdOn); freshly-created
                // rows are detail-shaped and expose createdOn. Fall back so both
                // render a date.
                Header: "Updated",
                accessor: (row: ITask) => row.lastUpdated ?? row.createdOn,
                id: "lastUpdated",
                // DoneDone already returns these pre-formatted (search: "Jul 17",
                // detail: "Jul 17, 2026 7:47 AM"). Render as-is: piping the
                // year-less search string through new Date() defaulted the year
                // to 2001.
                Cell: ({ value }: { value: string | null }) => value || "-",
            },
            {
                Header: "Project",
                accessor: "project",
                Cell: ({ value }: { value: { name: string } | null }) =>
                    value?.name ?? "-",
            },
        ];
    }, []);

    return (
        <Container fluid>
            <h1>Support Tickets (DoneDone 2)</h1>
            <Card className={styles.formBox}>
                <Form onSubmit={submitTask}>
                    <Label htmlFor="subject" style={{ display: "none" }}>
                        Subject
                    </Label>
                    <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={(e) => handleFormChange(e, setFormData)}
                        type="text"
                        placeholder="Subject"
                    />
                    <br />
                    <Label htmlFor="description" style={{ display: "none" }}>
                        Description
                    </Label>
                    <textarea
                        id="description"
                        className={`${styles.textArea} form-control`}
                        name="description"
                        value={formData.description}
                        onChange={(e) => handleFormChange(e, setFormData)}
                        rows={5}
                        placeholder="Description"
                    />
                    <br />
                    <div className={styles.submitBtnBox}>
                        <Button
                            className={styles.submitBtn}
                            color="primary"
                            type="submit"
                        >
                            Submit
                        </Button>
                    </div>
                </Form>
            </Card>

            {/* Active Issues */}
            <ReactTable
                data={tasks}
                columns={columns}
                showPagination
                isLoading={isLoading}
            />
        </Container>
    );
}
